import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  CheckIcon,
  FlagIcon,
  HeartIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  MapIcon,
  PlusIcon,
  UserCircleIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { contextService } from '../../services/api';

const typeOptions = [
  ['meeting', 'Meeting', CalendarDaysIcon],
  ['customer', 'Customer', BriefcaseIcon],
  ['person', 'Person', UserCircleIcon],
  ['project', 'Project', FlagIcon],
  ['goal', 'Goal', CheckIcon],
  ['trip', 'Trip', MapIcon],
  ['occasion', 'Occasion', HeartIcon],
  ['routine', 'Routine', ArrowPathIcon],
];

const contextIcon = (type) => typeOptions.find(([value]) => value === type)?.[2] || LinkIcon;
const contextLabel = (type) => typeOptions.find(([value]) => value === type)?.[1] || 'Context';

const ContextsPage = () => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    title: '',
    context_type: 'meeting',
    description: '',
    desired_outcome: '',
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => contextService.getContexts(),
  });
  const visibleContexts = useMemo(() => {
    const contexts = data?.contexts || [];
    const normalized = query.trim().toLowerCase();
    if (!normalized) return contexts;
    return contexts.filter((context) =>
      `${context.title} ${context.description || ''} ${context.desired_outcome || ''} ${context.context_type}`
        .toLowerCase()
        .includes(normalized)
    );
  }, [data?.contexts, query]);

  const createMutation = useMutation({
    mutationFn: () => contextService.createContext(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contexts'] });
      queryClient.invalidateQueries({ queryKey: ['today'] });
      setForm({ title: '', context_type: 'meeting', description: '', desired_outcome: '' });
      setShowCreate(false);
      toast.success('Context created');
    },
    onError: (createError) => toast.error(createError.response?.data?.detail || 'Could not create context'),
  });

  return (
    <div className="app-page">
      <div className="app-page-inner max-w-6xl">
        <header className="page-header">
          <div>
            <div className="eyebrow">
              <LinkIcon className="h-4 w-4" />
              Persistent workspaces
            </div>
            <h1 className="page-title">Contexts</h1>
            <p className="page-description">
              Organize information around meetings, customers, people, goals, projects, and situations instead of
              the applications where each fragment originated.
            </p>
          </div>
          <Link to="/plans" className="secondary-button">Open plans</Link>
          <button onClick={() => setShowCreate(true)} className="primary-button">
            <PlusIcon className="h-4 w-4" />
            New context
          </button>
        </header>

        <section className="surface-panel overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-[#e2e7e3] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-[#8a948e]" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="field-input pl-10"
                placeholder="Search meetings, people, projects, or goals"
                aria-label="Search contexts"
              />
            </div>
            <p className="text-xs font-semibold text-[#77827b]">
              {visibleContexts.length} active {visibleContexts.length === 1 ? 'context' : 'contexts'}
            </p>
          </div>

          {isLoading ? (
            <div className="grid gap-px bg-[#e2e7e3] md:grid-cols-2">
              {[1, 2, 3, 4].map((item) => <div key={item} className="h-48 animate-pulse bg-[#f4f6f4]" />)}
            </div>
          ) : error ? (
            <div className="px-6 py-16 text-center">
              <h2 className="text-base font-bold">Contexts unavailable</h2>
              <p className="mt-2 text-sm text-[#6b766f]">The context graph could not be loaded.</p>
              <button onClick={() => refetch()} className="secondary-button mt-5">Try again</button>
            </div>
          ) : visibleContexts.length === 0 ? (
            <div className="px-6 py-20 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-[#dcece4] text-[#246b52]">
                <LinkIcon className="h-6 w-6" />
              </span>
              <h2 className="mt-4 text-base font-bold">{query ? 'No matching contexts' : 'Create your first context'}</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#6b766f]">
                {query
                  ? 'Try a different search term.'
                  : 'Start with a meeting, customer, person, project, or goal that currently spans more than one app.'}
              </p>
              {!query && (
                <button onClick={() => setShowCreate(true)} className="primary-button mt-5">
                  <PlusIcon className="h-4 w-4" />
                  New context
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-px bg-[#e2e7e3] md:grid-cols-2">
              {visibleContexts.map((context) => {
                const Icon = contextIcon(context.context_type);
                return (
                  <Link
                    key={context.id}
                    to={`/contexts/${context.id}`}
                    className="group bg-white p-5 transition hover:bg-[#f8faf8] sm:p-6"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#e7efe9] text-[#246b52]">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="status-pill status-pill-success">{context.status}</span>
                    </div>
                    <p className="mt-6 text-[10px] font-bold uppercase text-[#7d8881]">{contextLabel(context.context_type)}</p>
                    <h2 className="mt-1 text-lg font-bold group-hover:text-[#1f6049]">{context.title}</h2>
                    <p className="mt-2 line-clamp-2 min-h-[48px] text-sm leading-6 text-[#66716a]">
                      {context.desired_outcome || context.description || 'Desired outcome not defined yet.'}
                    </p>
                    <div className="mt-6 flex items-center justify-between border-t border-[#e5eae6] pt-4 text-xs">
                      <span className="flex items-center gap-2 font-semibold text-[#5e6a63]">
                        <UsersIcon className="h-4 w-4" />
                        {context.people?.length || 0} people · {context.link_count} connected items
                      </span>
                      <span className="flex items-center gap-1 text-[#8a948e]">
                        {formatDistanceToNow(new Date(context.updated_at), { addSuffix: true })}
                        <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50">
          <button className="absolute inset-0 h-full w-full bg-[#101813]/50 backdrop-blur-sm" onClick={() => setShowCreate(false)} aria-label="Close create context" />
          <section className="modal-safe absolute inset-x-0 bottom-0 mx-auto max-w-2xl rounded-t-lg border border-b-0 border-[#dce3dd] bg-white shadow-[0_-20px_60px_rgba(21,36,28,0.18)] sm:bottom-5 sm:rounded-lg sm:border">
            <div className="flex items-start justify-between border-b border-[#e2e7e3] p-5">
              <div>
                <p className="text-[11px] font-bold uppercase text-[#246b52]">New workspace</p>
                <h2 className="mt-1 text-lg font-bold">Create a context</h2>
                <p className="mt-1 text-sm text-[#6b766f]">Name the situation, not the source application.</p>
              </div>
              <button onClick={() => setShowCreate(false)} className="icon-button" aria-label="Close">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 p-5">
              <div>
                <label htmlFor="context-title" className="field-label">Context name</label>
                <input
                  id="context-title"
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  className="field-input"
                  placeholder="Acme renewal meeting, Product launch, Dubai trip..."
                  autoFocus
                />
              </div>

              <div>
                <p className="field-label">Context type</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {typeOptions.map(([value, label, Icon]) => (
                    <button
                      key={value}
                      onClick={() => setForm((current) => ({ ...current, context_type: value }))}
                      className={`flex min-h-11 items-center gap-2 rounded-md border px-3 text-xs font-bold ${
                        form.context_type === value
                          ? 'border-[#8cb7a1] bg-[#edf5f0] text-[#1f6049]'
                          : 'border-[#dce3dd] text-[#66716a] hover:bg-[#f8faf8]'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="context-description" className="field-label">What is this about?</label>
                  <textarea
                    id="context-description"
                    value={form.description}
                    onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                    className="field-input resize-none"
                    rows={3}
                    placeholder="The situation and relevant background"
                  />
                </div>
                <div>
                  <label htmlFor="context-outcome" className="field-label">Desired outcome</label>
                  <textarea
                    id="context-outcome"
                    value={form.desired_outcome}
                    onChange={(event) => setForm((current) => ({ ...current, desired_outcome: event.target.value }))}
                    className="field-input resize-none"
                    rows={3}
                    placeholder="What should be true when this goes well?"
                  />
                </div>
              </div>

              <div className="flex justify-end border-t border-[#e7ebe8] pt-5">
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={!form.title.trim() || createMutation.isPending}
                  className="primary-button disabled:opacity-60"
                >
                  {createMutation.isPending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                  Create context
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ContextsPage;
